import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShieldCheck, 
  ShieldAlert, 
  AlertTriangle, 
  CheckCircle2, 
  ArrowRight, 
  Mail, 
  Key, 
  Lock, 
  FileText, 
  MessageSquareWarning, 
  Fingerprint, 
  CreditCard,
  ChevronRight,
  Sparkles,
  Info
} from 'lucide-react';
import { ScoreGauge } from '../components/common/ScoreGauge';
import { StatusIndicator } from '../components/common/StatusIndicator';
import { useStore } from '../store/useStore';
import { SecurityScoreCategory } from '../types';

export const SecurityScorePage: React.FC = () => {
  const { securityScore, openAiModal } = useStore();
  const [selectedCategory, setSelectedCategory] = useState<SecurityScoreCategory | null>(null);

  const getCategoryIcon = (id: string) => {
    switch (id) {
      case 'email': return <Mail className="w-5 h-5 text-emerald-400" />;
      case 'password': return <Key className="w-5 h-5 text-amber-400" />;
      case 'account': return <Lock className="w-5 h-5 text-cyan-400" />;
      case 'idDocuments': return <FileText className="w-5 h-5 text-blue-400" />;
      case 'scamExposure': return <MessageSquareWarning className="w-5 h-5 text-indigo-400" />;
      case 'identityExposure': return <Fingerprint className="w-5 h-5 text-rose-400" />;
      case 'creditMonitoring': return <CreditCard className="w-5 h-5 text-teal-400" />;
      default: return <ShieldCheck className="w-5 h-5 text-emerald-400" />;
    }
  };

  const categories = Object.values(securityScore.categories);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-950 text-emerald-300 text-xs font-semibold border border-emerald-800">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Identity Security Score Diagnostic</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white">
          Your Identity Security Score
        </h1>
        <p className="text-slate-400 text-sm max-w-xl mx-auto">
          A dynamic benchmark (0–100) assessing your exposure to data breaches, document protection level, account security, and scam resilience.
        </p>
      </div>

      {/* Main Score Hero Card */}
      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 sm:p-10 shadow-2xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex flex-col items-center sm:flex-row gap-6 text-center sm:text-left">
            <ScoreGauge score={securityScore.overallScore} size="lg" />
            <div className="space-y-2">
              <div className="flex items-center justify-center sm:justify-start gap-2">
                <StatusIndicator status={securityScore.status} showLabel />
                <span className="text-xs text-slate-500">• Updated Today</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white">
                {securityScore.overallScore} / 100
              </h2>
              <p className="text-sm text-slate-300 max-w-md leading-relaxed">
                {securityScore.headline}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            <Link
              to="/account-security"
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-lg shadow-emerald-950/40 transition-colors text-center flex items-center justify-center gap-2"
            >
              <span>Improve My Score</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={openAiModal}
              className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-medium text-xs transition-colors flex items-center justify-center gap-2"
            >
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span>Ask AI Improvement Advice</span>
            </button>
          </div>
        </div>
      </div>

      {/* 7 Categories Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-bold text-white">Security Score Breakdown by Category</h3>
          <span className="text-xs text-slate-400">Click any category for actionable guidance</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {categories.map((cat) => (
            <div
              key={cat.id}
              onClick={() => setSelectedCategory(cat)}
              className="p-6 rounded-2xl bg-slate-900 border border-slate-800 hover:border-slate-700 hover:bg-slate-900/80 cursor-pointer transition-all shadow-lg space-y-4 group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-slate-950 border border-slate-800 group-hover:scale-105 transition-transform">
                    {getCategoryIcon(cat.id)}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">
                      {cat.name}
                    </h4>
                    <p className="text-xs text-slate-400 font-medium">{cat.score} / {cat.maxScore} points</p>
                  </div>
                </div>
                <StatusIndicator status={cat.status} showLabel />
              </div>

              {/* Progress bar */}
              <div className="w-full bg-slate-950 rounded-full h-2 overflow-hidden border border-slate-800">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${
                    cat.score >= 80 ? 'bg-emerald-500' : cat.score >= 60 ? 'bg-amber-500' : 'bg-rose-500'
                  }`}
                  style={{ width: `${(cat.score / cat.maxScore) * 100}%` }}
                />
              </div>

              <p className="text-xs text-slate-300 leading-relaxed line-clamp-2">
                {cat.summary}
              </p>

              <div className="flex items-center justify-between pt-2 border-t border-slate-800/80 text-xs">
                <span className="text-slate-400 text-[11px]">Recommendation:</span>
                <span className="text-emerald-400 font-semibold flex items-center gap-1 group-hover:underline">
                  {cat.actionText}
                  <ChevronRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deep-dive Category Modal */}
      {selectedCategory && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl space-y-6">
            <div className="flex items-center justify-between border-b border-slate-800 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                  {getCategoryIcon(selectedCategory.id)}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">{selectedCategory.name}</h4>
                  <p className="text-xs text-slate-400">Score: {selectedCategory.score} / {selectedCategory.maxScore}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedCategory(null)}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="space-y-4 text-xs">
              <div>
                <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px]">Status Evaluation</span>
                <p className="text-slate-200 text-sm mt-1">{selectedCategory.summary}</p>
              </div>

              <div className="space-y-2 bg-slate-950 p-4 rounded-xl border border-slate-800">
                <span className="font-bold text-slate-300 uppercase tracking-wider text-[10px]">Diagnostic Checklist</span>
                {selectedCategory.details.map((detail, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-slate-300">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    <span>{detail}</span>
                  </div>
                ))}
              </div>

              <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-800/60 text-emerald-200 space-y-1">
                <span className="font-bold text-emerald-300 uppercase tracking-wider text-[10px]">How to Improve</span>
                <p className="text-xs">{selectedCategory.recommendation}</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setSelectedCategory(null)}
                className="px-4 py-2 rounded-xl text-slate-300 hover:text-white text-xs font-medium"
              >
                Close
              </button>
              <Link
                to={selectedCategory.actionUrl}
                onClick={() => setSelectedCategory(null)}
                className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors flex items-center gap-1"
              >
                <span>{selectedCategory.actionText}</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
